import { useEffect, useState } from 'react';
import { RefreshCw, CheckCircle, XCircle, Clock } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { AdminPageHeader } from '../../components/admin/AdminPageHeader';
import { Spinner } from '../../components/ui/Spinner';

type Timeframe = '7d' | '30d' | '90d';

interface DunningMetric {
  total_failures: number;
  recovered: number;
  given_up: number;
  in_progress: number;
  total_amount_brl: number;
  recovered_amount_brl: number;
  lost_amount_brl: number;
  recovery_rate: number;
  avg_retries_to_recover: number;
}

interface FailedPayment {
  id: string;
  created_at: string;
  amount: number;
  status: string;
  retry_count: number;
  max_retries: number;
  next_retry_at: string | null;
  profiles: { email: string | null; full_name: string | null } | null;
}

const STATUS_STYLE: Record<string, { bg: string; color: string; label: string }> = {
  pending:   { bg: 'rgba(59,130,246,0.12)',  color: '#60a5fa',  label: 'Pendente' },
  retrying:  { bg: 'rgba(251,191,36,0.12)',  color: '#fbbf24',  label: 'Retentando' },
  recovered: { bg: 'rgba(74,222,128,0.12)',  color: '#4ade80',  label: 'Recuperado' },
  given_up:  { bg: 'rgba(239,68,68,0.12)',   color: '#f87171',  label: 'Desistido' },
};

export function AdminDunningPage() {
  const [timeframe, setTimeframe] = useState<Timeframe>('30d');
  const [metrics, setMetrics] = useState<DunningMetric | null>(null);
  const [payments, setPayments] = useState<FailedPayment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, [timeframe]);

  async function load() {
    setLoading(true);

    const days = timeframe === '7d' ? 7 : timeframe === '90d' ? 90 : 30;
    const since = new Date();
    since.setDate(since.getDate() - days);
    const sinceIso = since.toISOString();

    const [metricsRes, paymentsRes] = await Promise.all([
      supabase
        .from('dunning_metrics')
        .select('total_failures, recovered, given_up, in_progress, total_amount_brl, recovered_amount_brl, lost_amount_brl, recovery_rate, avg_retries_to_recover')
        .gte('created_at', sinceIso)
        .single(),
      supabase
        .from('failed_payments')
        .select('id, created_at, amount, status, retry_count, max_retries, next_retry_at, profiles:user_id(email, full_name)')
        .gte('created_at', sinceIso)
        .order('created_at', { ascending: false })
        .limit(100),
    ]);

    setMetrics((metricsRes.data ?? null) as DunningMetric | null);
    setPayments((paymentsRes.data ?? []) as unknown as FailedPayment[]);
    setLoading(false);
  }

  const m = metrics;
  const recoveryRate = m?.recovery_rate ?? 0;
  const recoveryColor = recoveryRate >= 70 ? '#4ade80' : recoveryRate >= 50 ? '#fbbf24' : '#f87171';

  const fmtBrl = (val: number | null | undefined) =>
    `R$ ${(val ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

  const kpiCards = [
    {
      icon: <XCircle size={18} />,
      label: 'Total Falhas',
      value: (m?.total_failures ?? 0).toLocaleString('pt-BR'),
      sub: fmtBrl(m?.total_amount_brl),
    },
    {
      icon: <CheckCircle size={18} />,
      label: 'Recuperados',
      value: (m?.recovered ?? 0).toLocaleString('pt-BR'),
      sub: fmtBrl(m?.recovered_amount_brl),
    },
    {
      icon: <RefreshCw size={18} />,
      label: 'Taxa de Recuperação',
      value: `${recoveryRate.toFixed(1)}%`,
      sub: `Média de ${(m?.avg_retries_to_recover ?? 0).toFixed(1)} tentativas`,
    },
    {
      icon: <Clock size={18} />,
      label: 'Perdidos',
      value: (m?.given_up ?? 0).toLocaleString('pt-BR'),
      sub: fmtBrl(m?.lost_amount_brl),
    },
  ];

  return (
    <div>
      <AdminPageHeader
        title="Recuperação de Pagamentos (Dunning)"
        objective="Acompanhe falhas de cobrança, tentativas de recuperação automática e valor recuperado. Use para avaliar a eficácia do ciclo de dunning e identificar usuários em risco de churn por inadimplência."
      />

      {/* Timeframe Buttons */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
        {(['7d', '30d', '90d'] as Timeframe[]).map(tf => (
          <button
            key={tf}
            className={timeframe === tf ? 'btn btn-primary btn-sm' : 'btn btn-ghost btn-sm'}
            onClick={() => setTimeframe(tf)}
          >
            {tf}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
          <Spinner />
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
            {kpiCards.map(card => (
              <div key={card.label} className="card" style={{ padding: '1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', color: 'var(--text-muted)' }}>
                  {card.icon}
                  <span style={{ fontSize: 'var(--text-caption)' }}>{card.label}</span>
                </div>
                <p style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 0.25rem' }}>{card.value}</p>
                {card.sub && (
                  <p style={{ margin: 0, fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>{card.sub}</p>
                )}
              </div>
            ))}
          </div>

          {/* Recovery Rate Progress Bar */}
          <div className="card" style={{ padding: '1.25rem', marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>Taxa de Recuperação Global</span>
              <span style={{ fontSize: 'var(--text-label)', fontWeight: 700, color: recoveryColor }}>
                {recoveryRate.toFixed(1)}%
              </span>
            </div>
            <div style={{ height: '8px', borderRadius: '999px', backgroundColor: 'var(--bg-surface)', overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: `${Math.min(recoveryRate, 100)}%`,
                borderRadius: '999px',
                backgroundColor: recoveryColor,
                transition: 'width 0.4s ease',
              }} />
            </div>
            <p style={{ margin: '0.4rem 0 0', fontSize: '11px', color: 'var(--text-muted)' }}>
              {recoveryRate >= 70
                ? 'Excelente taxa de recuperação'
                : recoveryRate >= 50
                  ? 'Taxa de recuperação moderada, considere ajustar os intervalos de retry'
                  : 'Taxa de recuperação baixa, revise as regras de dunning'}
            </p>
          </div>

          {/* Failed Payments Table */}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid var(--border-subtle)' }}>
              <h2 style={{ margin: 0, fontSize: 'var(--text-label)', fontWeight: 600, color: 'var(--text-primary)' }}>
                Falhas Recentes
              </h2>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Usuário', 'Valor', 'Status', 'Tentativas', 'Próximo Retry', 'Falhou em'].map(col => (
                    <th key={col} style={{
                      textAlign: 'left',
                      padding: '0.625rem 1rem',
                      fontSize: 'var(--text-caption)',
                      fontWeight: 600,
                      color: 'var(--text-muted)',
                      backgroundColor: 'var(--bg-surface)',
                      borderBottom: '1px solid var(--border-subtle)',
                    }}>
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {payments.map(row => {
                  const badge = STATUS_STYLE[row.status] ?? STATUS_STYLE.pending;
                  return (
                    <tr key={row.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      <td style={{ padding: '0.625rem 1rem' }}>
                        <p style={{ margin: 0, fontSize: 'var(--text-label)', color: 'var(--text-primary)', fontWeight: 600 }}>
                          {row.profiles?.full_name ?? '-'}
                        </p>
                        <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-muted)' }}>
                          {row.profiles?.email ?? '-'}
                        </p>
                      </td>
                      <td style={{ padding: '0.625rem 1rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                        {fmtBrl(row.amount)}
                      </td>
                      <td style={{ padding: '0.625rem 1rem' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: '999px',
                          fontSize: '11px',
                          fontWeight: 600,
                          backgroundColor: badge.bg,
                          color: badge.color,
                        }}>
                          {badge.label}
                        </span>
                      </td>
                      <td style={{ padding: '0.625rem 1rem', fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>
                        {row.retry_count} / {row.max_retries}
                      </td>
                      <td style={{ padding: '0.625rem 1rem', fontSize: 'var(--text-caption)', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                        {row.next_retry_at
                          ? new Date(row.next_retry_at).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
                          : '-'}
                      </td>
                      <td style={{ padding: '0.625rem 1rem', fontSize: 'var(--text-caption)', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                        {new Date(row.created_at).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                      </td>
                    </tr>
                  );
                })}
                {payments.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-muted)', fontStyle: 'italic', padding: '2rem' }}>
                      Nenhuma falha de pagamento encontrada no período
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
